import { useState } from 'react';
import { GoogleLogin } from './GoogleLogin';
import axios from 'axios';
import { useAuth } from '../../hooks/useAuth';
import { Footer } from '../Layout/Footer';

const RAW_API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002';
const API_BASE = RAW_API_BASE.replace(/\/$/, '').endsWith('/api')
  ? RAW_API_BASE.replace(/\/$/, '')
  : `${RAW_API_BASE.replace(/\/$/, '')}/api`;

interface LoginScreenProps {
  onLoginSuccess: () => void;
}

export const LoginScreen: React.FC<LoginScreenProps> = ({ onLoginSuccess }) => {
  const [error, setError] = useState<string>('');
  const { login } = useAuth();
  const [devEmail, setDevEmail] = useState('dev@creativedock.com');
  const [devLoading, setDevLoading] = useState(false);

  const handleSuccess = () => {
    setError('');
    onLoginSuccess();
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleDevLogin = async () => {
    setDevLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_BASE}/auth/dev-login`, {
        email: devEmail,
        name: devEmail.split('@')[0]
      });
      login(response.data.token, response.data.user);
      onLoginSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Dev login failed');
    } finally {
      setDevLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-bg flex items-center justify-center p-6">
      <div className="fixed inset-0 bg-gradient-to-br from-gray-900 via-dark-bg to-gray-800 -z-10"></div>
      
      <div className="bg-dark-card border border-dark-border rounded-2xl p-8 shadow-xl w-full max-w-md animate-fade-in">
        <div className="text-center mb-8">
          <div className="bg-brand-gradient text-white px-6 py-3 rounded-full inline-flex items-center gap-3 mb-6">
            <img
              src={`${import.meta.env.BASE_URL || '/'}branding/logo-full.svg`}
              alt=""
              className="h-6"
              onError={(e) => (e.currentTarget.style.display = 'none')}
            />
            <span className="font-bold text-lg">Talent First</span>
          </div>
          <h1 className="text-3xl font-bold mb-3 text-dark-text">Welcome</h1>
          <p className="text-dark-text-secondary">
            Sign in with your Google Workspace account to continue
          </p>
        </div>

        <div className="mb-6 flex justify-center">
          <GoogleLogin onSuccess={handleSuccess} onError={handleError} />
        </div>

        {/* Dev login bypass - only in development */}
        {import.meta.env.DEV && (
          <div className="mt-4 pt-4 border-t border-dark-border">
            <p className="text-xs text-dark-text-secondary mb-2 text-center">Development Login</p>
            <div className="flex gap-2">
              <input
                type="email"
                value={devEmail}
                onChange={(e) => setDevEmail(e.target.value)}
                className="flex-1 px-3 py-2 bg-dark-bg border border-dark-border rounded-lg text-sm text-dark-text focus:outline-none focus:border-blue-500"
                placeholder="dev@creativedock.com"
              />
              <button
                onClick={handleDevLogin}
                disabled={devLoading || !devEmail}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white text-sm rounded-lg transition-colors"
              >
                {devLoading ? '...' : 'Dev Login'}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 p-4 bg-red-900/30 border border-red-500 rounded-lg text-red-200 text-sm">
            <p className="font-semibold mb-1">Login Failed</p>
            <p>{error}</p>
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-dark-border">
          <div className="text-sm text-dark-text-secondary">
            <p className="mb-2 font-semibold">Security & Access</p>
            <ul className="text-xs space-y-1 ml-6">
              <li>Google Workspace authentication</li>
              <li>Verified email domain access</li>
              <li>All actions are logged</li>
              <li>Session expires after 24 hours</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="fixed bottom-0 left-0 right-0">
        <Footer />
      </div>
    </div>
  );
};
